import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { APP_LOGO, BRAND_WORDMARK } from '../../lib/brand';
import { BRANDING } from '../../lib/branding';
import {
  LayoutDashboard,
  Users,
  Package,
  FileText,
  ReceiptIndianRupee,
  Inbox,
  ChevronLeft,
  ChevronRight,
  Calendar,
  UserCog,
  Loader2,
  X,
  Menu,
  CreditCard,
  Percent,
  BarChart3,
  Target,
  Send,
  Settings,
  Ticket
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { useUI, useStore } from '../../store/useStore';
import { Button } from '../ui/button';
import { useAuth } from '../../hooks/useAuth';

// Definiamo la lista completa di tutte le possibili voci del menu con i colori specifici
const allNavigationItems = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, roles: ['admin', 'sales', 'commerciale', 'amministrazione', 'lettore', 'warehouse'], color: 'neutral' },
  { name: 'Clienti', href: '/customers', icon: Users, roles: ['admin', 'sales', 'commerciale', 'amministrazione'], color: 'brand' },
  { name: 'Lead', href: '/leads', icon: Target, roles: ['admin', 'sales', 'commerciale', 'amministrazione'], color: 'amber' },
  { name: 'Prodotti', href: '/products', icon: Package, roles: ['admin', 'sales', 'commerciale', 'amministrazione', 'lettore', 'warehouse'], color: 'green' },
  { name: 'Offerte', href: '/price-lists', icon: FileText, roles: ['admin', 'sales', 'commerciale', 'amministrazione'], color: 'purple' },
  { name: 'Fatture', href: '/invoices', icon: ReceiptIndianRupee, roles: ['admin', 'commerciale', 'amministrazione'], color: 'violet' },
  { name: 'Passività', href: '/passive-invoices', icon: Inbox, roles: ['admin', 'commerciale', 'amministrazione'], color: 'pink' },
  { name: 'Subscriptions', href: '/subscriptions', icon: CreditCard, roles: ['admin', 'commerciale', 'amministrazione', 'sales'], color: 'lime' },
  { name: 'Analisi aziendali', href: '/analisi-aziendali', icon: BarChart3, roles: ['admin', 'commerciale', 'amministrazione'], color: 'orange' },
  { name: 'Calendario', href: '/calendar', icon: Calendar, roles: ['admin', 'sales', 'commerciale', 'amministrazione'], color: 'yellow' },
  { name: 'Provvigioni', href: '/commissions', icon: Percent, roles: ['admin', 'commerciale', 'amministrazione'], color: 'emerald' },
  { name: 'Mailing', href: '/mailing', icon: Send, roles: ['admin', 'sales', 'commerciale', 'amministrazione'], color: 'fuchsia' },
  { name: 'Eventi', href: '/eventi', icon: Ticket, roles: ['admin', 'commerciale'], color: 'amber' },

  { name: 'Gestione Utenti', href: '/user-management', icon: UserCog, roles: ['admin'], color: 'neutral' },
  // Per tutti: ognuno ci cambia la propria password. Le parti sull'azienda le vede solo l'amministratore.
  { name: 'Impostazioni', href: '/impostazioni', icon: Settings, roles: ['admin', 'sales', 'commerciale', 'amministrazione', 'lettore', 'warehouse'], color: 'neutral' }
] as const;

export function Sidebar() {
  const location = useLocation();
  const { sidebarCollapsed, setSidebarCollapsed, mobileMenuOpen, setMobileMenuOpen } = useUI();
  const { profile, loading } = useAuth(); // Prendiamo anche lo stato di 'loading'
  const isUpdateAvailable = useStore((state) => state.isUpdateAvailable);
  const newLeadCount = useStore((state) => state.newLeadCount);
  const subscriptionDraftCount = useStore((state) => state.subscriptionDraftCount);
  const newTicketOrderCount = useStore((state) => state.newTicketOrderCount);

  // Filtriamo i link solo quando il profilo è caricato
  const visibleNavigation = useMemo(() => {
    if (!profile?.role) return [];



    const userRole = profile.role as string;
    return allNavigationItems.filter(item => item.roles.includes(userRole));
  }, [profile?.role]);

  return (
    <>
      {/* Backdrop for mobile */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      <div className={cn(
        "liquid-glass-background border-r border-white/10 transition-all duration-300 ease-in-out shadow-xl z-[70]",
        "fixed inset-y-0 left-0 transform md:relative md:translate-x-0 h-screen",
        !mobileMenuOpen && "max-md:hidden",
        mobileMenuOpen ? "translate-x-0" : "-translate-x-full",
        sidebarCollapsed ? "w-20" : "w-64"
      )}>
        <div className="flex flex-col h-full">
          {/* Header con logo e pulsante */}
          <div className="flex items-center h-20 p-4 border-b border-white/10">
            {!sidebarCollapsed && (
              <div className="flex justify-center flex-1">
                <img src={BRAND_WORDMARK} alt={BRANDING.nomeBreve} className="w-36 h-14 object-contain" />
              </div>
            )}
            {sidebarCollapsed && (
              <div className="flex justify-center">
                <img src={BRAND_WORDMARK} alt={BRANDING.nomeBreve} className="w-14 h-10 object-contain" />
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className={cn("text-neutral-400 hover:text-white hover:bg-white/10 hidden md:flex", !sidebarCollapsed && "ml-auto")}
            >
              {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            </Button>

            {/* Close button for mobile */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(false)}
              className="md:hidden ml-auto text-neutral-400 hover:text-white"
            >
              <X className="w-6 h-6" />
            </Button>
          </div>

          {/* Navigazione */}
          <nav className="flex-1 p-4 overflow-y-auto">
            {/* LA CORREZIONE CHIAVE È QUI */}
            {loading ? (
              <div className="flex justify-center pt-8">
                <Loader2 className="w-6 h-6 animate-spin text-neutral-500" />
              </div>
            ) : (
              <ul className="space-y-2">
                {visibleNavigation.map((item) => {
                  const isActive = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href));

                  // Funzione per ottenere le classi CSS basate sul colore
                  /**
                   * Ogni voce di menu ha un colore proprio: non e' branding,
                   * e' una codifica di categoria che aiuta a ritrovare la
                   * sezione a colpo d'occhio. Per SLAB sono sparite le quattro
                   * tinte azzurre (blue, cyan, indigo, teal): "Clienti" prende
                   * il rosso di marca, le altre tre passano ad ambra, viola e
                   * lime per restare distinguibili fra loro.
                   */
                  const getColorClasses = (color: string, isActive: boolean) => {
                    const colorMap = {
                      brand: isActive ? "bg-primary-600 text-white" : "text-neutral-400 hover:bg-primary-600/20 hover:text-primary-400",
                      amber: isActive ? "bg-amber-600 text-white" : "text-neutral-400 hover:bg-amber-600/20 hover:text-amber-400",
                      green: isActive ? "bg-green-600 text-white" : "text-neutral-400 hover:bg-green-600/20 hover:text-green-400",
                      purple: isActive ? "bg-purple-600 text-white" : "text-neutral-400 hover:bg-purple-600/20 hover:text-purple-400",
                      orange: isActive ? "bg-orange-600 text-white" : "text-neutral-400 hover:bg-orange-600/20 hover:text-orange-400",
                      emerald: isActive ? "bg-emerald-600 text-white" : "text-neutral-400 hover:bg-emerald-600/20 hover:text-emerald-400",
                      violet: isActive ? "bg-violet-600 text-white" : "text-neutral-400 hover:bg-violet-600/20 hover:text-violet-400",
                      lime: isActive ? "bg-lime-600 text-white" : "text-neutral-400 hover:bg-lime-600/20 hover:text-lime-400",
                      yellow: isActive ? "bg-yellow-600 text-white" : "text-neutral-400 hover:bg-yellow-600/20 hover:text-yellow-400",
                      pink: isActive ? "bg-pink-600 text-white" : "text-neutral-400 hover:bg-pink-600/20 hover:text-pink-400",
                      fuchsia: isActive ? "bg-fuchsia-600 text-white" : "text-neutral-400 hover:bg-fuchsia-600/20 hover:text-fuchsia-400",
                      neutral: isActive ? "bg-neutral-700 text-white" : "text-neutral-400 hover:bg-neutral-800 hover:text-white"
                    };
                    return colorMap[color as keyof typeof colorMap] || colorMap.neutral;
                  };

                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={cn(
                          "relative flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 group",
                          sidebarCollapsed && "md:justify-center",
                          getColorClasses(item.color, isActive)
                        )}
                        title={sidebarCollapsed ? item.name : undefined}
                      >
                        <item.icon className={cn("flex-shrink-0 w-5 h-5", !sidebarCollapsed && "md:mr-3")} />
                        <span className={cn("truncate", sidebarCollapsed && "md:hidden", "ml-3 md:ml-0")}>{item.name}</span>
                        {item.href === '/leads' && newLeadCount > 0 && (
                          <span
                            className={cn(
                              "flex-shrink-0 rounded-full bg-red-500 animate-pulse",
                              sidebarCollapsed
                                ? "absolute top-1.5 right-1.5 w-2 h-2"
                                : "ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white"
                            )}
                            title={`${newLeadCount} nuovi lead`}
                          >
                            {!sidebarCollapsed && (newLeadCount > 9 ? '9+' : newLeadCount)}
                          </span>
                        )}
                        {/* Come i lead: qualcuno ha appena comprato biglietti sul sito. */}
                        {item.href === '/eventi' && newTicketOrderCount > 0 && (
                          <span
                            className={cn(
                              "flex-shrink-0 rounded-full bg-red-500 animate-pulse",
                              sidebarCollapsed
                                ? "absolute top-1.5 right-1.5 w-2 h-2"
                                : "ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white"
                            )}
                            title={`${newTicketOrderCount} nuovi ordini di biglietti`}
                          >
                            {!sidebarCollapsed && (newTicketOrderCount > 9 ? '9+' : newTicketOrderCount)}
                          </span>
                        )}
                        {/* Ambra, non rosso: sono bozze da emettere, lavoro da fare e non un'urgenza. */}
                        {item.href === '/invoices' && subscriptionDraftCount > 0 && (
                          <span
                            className={cn(
                              "flex-shrink-0 rounded-full bg-amber-500",
                              sidebarCollapsed
                                ? "absolute top-1.5 right-1.5 w-2 h-2"
                                : "ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white"
                            )}
                            title={`${subscriptionDraftCount} bozze di abbonamento da emettere`}
                          >
                            {!sidebarCollapsed && (subscriptionDraftCount > 9 ? '9+' : subscriptionDraftCount)}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </nav>


          {/* Footer con logo Pixel */}
          <div className="border-t border-white/10 p-4">
            {sidebarCollapsed ? (
              <div className="flex justify-center flex-col items-center gap-2">
                <img src={APP_LOGO} alt="Pixel" className="h-5 w-auto filter brightness-110" />
                {isUpdateAvailable && (
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" title="Aggiornamento disponibile" />
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-3">
                <img src={APP_LOGO} alt="Pixel" className="h-5 w-auto filter brightness-110" />
                <div className="flex flex-col">
                  <span className="text-xs text-neutral-400 leading-none">{`Pixel v ${typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '1.0.0'}`}</span>
                  {isUpdateAvailable && (
                    <button
                      onClick={() => {
                        if ('serviceWorker' in navigator) {
                          navigator.serviceWorker.getRegistrations().then((registrations) => {
                            Promise.all(registrations.map(reg => reg.unregister())).then(() => {
                              window.location.reload();
                            });
                          });
                        } else {
                          window.location.reload();
                        }
                      }}
                      className="text-[10px] text-red-400 font-bold hover:text-red-300 animate-pulse text-left mt-1 flex items-center gap-1"
                    >
                      <span>Aggiorna ora</span>
                      <Loader2 className="w-3 h-3 animate-spin" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};